// The ONE source of Symphony agent-task bucketing, mirroring native's `AgentTaskBucket`
// (native-apps/ios/Symphony/Navigation/AppRouter.swift) 1:1. Every count that fronts a tab — the
// workspace "Agents at work" stat cards AND the Agent Tasks tabs — must come from here.
//
// Native shipped, and fixed, the same class of bug this replaces: hand-rolled per-surface bucketing
// filed FAILED/CANCELLED under In progress and skipped the recency window, so a stat card promised a
// count the tab it opened could not show.
//
// Status-string compared (not enum) so both the raw `AgentTask` (workspace card) and the widened
// `WebTask` (`DigestedTaskStatus | AgentTaskStatus`) fall through cleanly.

import { isNotStartedTask, isScheduledTask, notBeforeMs, type SchedulableTask } from './task-schedule';

export type AgentTaskBucket = 'waiting' | 'inProgress' | 'done' | 'failed';

/**
 * Every bucket, in tab order. This is the addressable set (`?tab=`, counts, bucketing) — NOT what the
 * strip renders. For that, see `visibleBuckets`.
 */
export const AGENT_TASK_BUCKETS: AgentTaskBucket[] = ['waiting', 'inProgress', 'done', 'failed'];

/** The three buckets that are always rendered — `failed` is the exceptional one and joins conditionally. */
const ALWAYS_VISIBLE_BUCKETS: AgentTaskBucket[] = ['waiting', 'inProgress', 'done'];

/**
 * The bucket's BI token — what a tab-switch click reports as its `entity_id`.
 *
 * NOT the same string as the TS union: web spells the middle bucket `inProgress` (a camelCase
 * identifier) while the shared BI vocabulary is `in-progress`, which is what the native apps'
 * `AgentTaskBucket.slug` emits and what the route arg carries. The tab funnel only joins across
 * web/iOS/Android if all three send the identical token, so this mapping is the seam — never
 * report the raw union value. Pinned by `test/bi-entity-parity.test.ts`.
 */
export const AGENT_TASK_BUCKET_BI_SLUG: Record<AgentTaskBucket, string> = {
  waiting: 'waiting',
  inProgress: 'in-progress',
  done: 'done',
  failed: 'failed',
};

/** The minimum an entity needs to be bucketed. Satisfied by both `AgentTask` and `WebTask`. */
export interface BucketableTask extends SchedulableTask {
  status?: string;
  updatedDate?: string | Date;
  // Is an agent loop executing on this task RIGHT NOW? Both names carry the same verdict, computed by the
  // same rule (`taskRunState` in common-lib, off the conversation's persisted `pendingPredictionId`); they
  // differ only in which payload stamped it. `runInFlight` is the LIST's field (the `tasks` bridge action
  // and `/api/tasks`); `isLive` is the older SINGLE-task field, kept because the task-detail read and both
  // native clients already consume that name. Absent on a payload that computed neither.
  runInFlight?: boolean;
  isLive?: boolean;
  /**
   * The open questions this task is blocked on (`readTaskDecisionPrompts`, common-lib). Only the id is
   * needed to BUCKET a row — the rest of the prompt is the row's business — so the shape is the
   * structural minimum, which keeps this module free of the app's dependency graph exactly as its
   * `SchedulableTask` import already is.
   *
   * **Absent or non-empty, NEVER `[]`.** See {@link awaitsDecision} for why the bucket reads it at all.
   */
  decisionPrompts?: { decisionId: string }[];
  /** True iff the task names a blocker we could not resolve into a question. Absent otherwise — never
   *  `false`. Deliberately NOT part of {@link awaitsDecision}; see there. */
  decisionPromptsUnavailable?: boolean;
}

/**
 * Whether this row is holding an OPEN question at the user — the property that makes it Action-needed
 * whatever its status says.
 *
 * **This is not the same thing as `ACTION_NEEDED`, and that is the whole reason it exists.** Releasing a
 * decision edge patches `blockedByDecisions` under a field mask of exactly that one field
 * (`AgentTaskDecisionEdges.java`): the backend **never writes the task's status** on either the block or
 * the release. A goal-plan task blocked on a question is therefore almost always `PENDING`, which
 * `bucketContaining` files — correctly, for every other purpose — under In progress. So a screen that
 * filtered "waiting" by status found nearly NONE of the tasks that were literally waiting for the reader
 * to answer something, and the one bucket built to mean "this needs you" was the one place they weren't.
 *
 * `decisionPromptsUnavailable` deliberately does NOT count. It is the third state of the tri-state — a
 * blocker we could not READ — and a failed decoration read must not move rows between tabs: the count
 * would then swing on the health of a best-effort join, and the row it moved cannot be answered where it
 * landed anyway. Such a row keeps its status's bucket and says on its face that the question could not be
 * loaded, with a way through to the goal. (Reported as an under-specification of the brief; the three
 * surfaces implement it identically.)
 */
export function awaitsDecision(task: BucketableTask): boolean {
  return (task.decisionPrompts?.length ?? 0) > 0;
}

/**
 * Which bucket a status files under. Mirrors native `AgentTaskBucket.containing`:
 * ACTION_NEEDED and PAUSED → waiting (Action needed); COMPLETED / CANCELLED / MOOT → done (all terminal);
 * FAILED → its own bucket (terminal, but must not read as a successful "Done"); everything else —
 * including PENDING, IN_PROGRESS, WAITING, IN_REVIEW and any unknown — → inProgress.
 *
 * PENDING is deliberately inProgress, not waiting: a queued task is the agent's turn, not the user's.
 *
 * MOOT is deliberately NOT left to the `inProgress` fall-through: a retired task — one the world moved
 * past before the agent got to it — would otherwise read as live work on every count and every tab. It
 * carries its own quiet "Retired" chip (see `taskStatusBadge`) so it never reads as accomplished either.
 */
export function bucketContaining(status: string | undefined): AgentTaskBucket {
  switch (status) {
    case 'ACTION_NEEDED':
    case 'PAUSED':
      return 'waiting';
    case 'COMPLETED':
    case 'CANCELLED':
    case 'MOOT':
      return 'done';
    case 'FAILED':
      return 'failed';
    default:
      return 'inProgress';
  }
}

/**
 * The bucket for a whole ROW — its status's bucket, EXCEPT that an open question the user has to answer
 * promotes an In-progress row to `waiting`. See {@link awaitsDecision} for why the status cannot be
 * trusted here.
 *
 * **Only `inProgress` promotes.** `waiting` is already the answer; and a terminal row — `done` or
 * `failed` — stays terminal, because a stale payload carrying a prompt on a finished task must never
 * resurrect it into Action-needed. (The server already refuses to emit prompts for an ended task:
 * `ENDED_TASK_STATUSES` in common-lib. This is the client saying the same thing in its own voice rather
 * than trusting a decoration to be well-formed.)
 *
 * Every count and every tab goes through here, so the promotion reaches the Tasks tabs, their pills, the
 * workspace stat cards, the island's orange card and the origin fold's "needs you" count in one place.
 * The one number it CANNOT reach by itself is the island's header count, which is summed from a
 * different fetch — `/api/workspace-tasks` — and that route now runs the same decision join for exactly
 * this reason. A count that disagrees with its own list has shipped on this surface before.
 */
export function bucketOfTask(task: BucketableTask): AgentTaskBucket {
  const bucket = bucketContaining(task.status);
  if (bucket === 'inProgress' && awaitsDecision(task)) return 'waiting';
  return bucket;
}

/**
 * Whether a task has a live agent loop executing RIGHT NOW — the island's blue "in motion" signal, and
 * the task-detail spinner. Prefer whichever run-state field the payload carries and fall back to the
 * `inProgress` bucket only when it carries neither, so a payload that never computed run state can't
 * regress. The point of the signal is that a PAUSED / queued-`PENDING` / dead-loop task — all of which
 * bucket as `inProgress` — is NOT in motion once the real verdict is present.
 *
 * **Reading BOTH names is load-bearing, not defensive clutter.** The list stamps `runInFlight` and the
 * single-task read stamps `isLive`; checking only the older one is how the island kept counting a task
 * that had been `IN_PROGRESS` with nobody on it for seventeen days — the list carried the correct answer
 * under the other name and this function never looked at it.
 */
export function isInMotion(task: BucketableTask, now: number = Date.now()): boolean {
  if (typeof task.runInFlight === 'boolean') {
    return task.runInFlight;
  }
  if (typeof task.isLive === 'boolean') {
    return task.isLive;
  }
  // A scheduled task is queued for a date in the future, so nothing can be running on it — the bucket
  // fallback below would otherwise turn the island blue for work that starts next Thursday. A task nobody
  // has ever been put on is the same class of mistake without the date: no agent has ever held it, so
  // there is no loop to be in flight.
  if (isScheduledTask(task, now) || isNotStartedTask(task, now)) return false;
  // Via `bucketOfTask`, deliberately — so a row blocked on an open question, which that function moves to
  // `waiting`, falls out of the fallback too. A task the agent cannot proceed with until somebody answers
  // is the last row that should turn the island blue on a guess.
  return bucketOfTask(task) === 'inProgress';
}

/**
 * Scheduled — DERIVED, never a status: a PENDING task whose `notBefore` is still in the future. It stays
 * in the In-progress bucket (it IS pending work, and it is counted there), but within that list it sorts
 * after the live rows and renders under the quiet "Later" section header. The rule itself lives in
 * `task-schedule` so the card, the list and this map cannot drift apart on it.
 */
export function isScheduled(task: BucketableTask, now: number = Date.now()): boolean {
  return isScheduledTask(task, now);
}

/**
 * Not started — DERIVED, never a status, and the sibling of {@link isScheduled}: a PENDING task with no
 * date and no agent ever put on it. It stays in the In-progress bucket and in its count (it IS pending
 * work); it only stops CLAIMING that an agent is working on it. The rule lives in `task-schedule` so the
 * card, the list and the island cannot drift apart on it.
 */
export function isNotStarted(task: BucketableTask, now: number = Date.now()): boolean {
  return isNotStartedTask(task, now);
}

/**
 * The rows of one list split into what is live now and what is waiting for its date. `live` keeps the
 * caller's order; `later` is ordered by when it will happen (soonest first), which is the only ordering
 * that means anything for work that has not started. Copies — the caller's array is very often a React
 * Query cache entry.
 */
export function splitScheduled<T extends BucketableTask>(tasks: T[], now: number = Date.now()): { live: T[]; later: T[] } {
  const live: T[] = [];
  const later: T[] = [];
  for (const task of tasks) (isScheduledTask(task, now) ? later : live).push(task);
  later.sort((a, b) => (notBeforeMs(a) ?? 0) - (notBeforeMs(b) ?? 0));
  return { live, later };
}

/** Terminal-with-a-problem: the `failed` bucket. Surfaced on its own Failed tab, NOT in the island's
 *  waiting count — a finished task is not waiting on the user, and nothing they do could clear it. */
export function isFailed(task: BucketableTask): boolean {
  return bucketOfTask(task) === 'failed';
}

/** The recency window every agent-task surface shares — native `AgentTaskBucket.recentWindow`. */
export const RECENT_WINDOW_MS = 30 * 86400 * 1000;

/**
 * Tasks updated within the last 30 days. A missing/unparseable `updatedDate` counts as recent, so a
 * task can never be hidden by absent metadata. `now` is injectable for tests.
 */
export function recentTasks<T extends BucketableTask>(tasks: T[], now: number = Date.now()): T[] {
  const cutoff = now - RECENT_WINDOW_MS;
  return tasks.filter((task) => {
    if (!task.updatedDate) return true;
    const updated = new Date(task.updatedDate).getTime();
    return Number.isNaN(updated) || updated >= cutoff;
  });
}

/** The windowed tasks split by bucket — the shape both surfaces render from. */
export function bucketTasks<T extends BucketableTask>(
  tasks: T[],
  now: number = Date.now(),
): Record<AgentTaskBucket, T[]> {
  const byBucket: Record<AgentTaskBucket, T[]> = { waiting: [], inProgress: [], done: [], failed: [] };
  for (const task of recentTasks(tasks, now)) byBucket[bucketOfTask(task)].push(task);
  return byBucket;
}

export function countInBucket(tasks: BucketableTask[], bucket: AgentTaskBucket, now: number = Date.now()): number {
  return recentTasks(tasks, now).filter((task) => bucketOfTask(task) === bucket).length;
}

/**
 * Whether the task has finished, one way or the other — `done` (COMPLETED / CANCELLED) or `failed`.
 * The complement is work the agents are still holding: `waiting` (the user's turn) and `inProgress`.
 */
export function isSettled(task: BucketableTask): boolean {
  const bucket = bucketOfTask(task);
  return bucket === 'done' || bucket === 'failed';
}

/** `updatedDate` as a sortable number. Missing or unparseable sorts last, never crashes the comparator. */
function updatedMs(task: BucketableTask): number {
  if (!task.updatedDate) return 0;
  const ms = new Date(task.updatedDate).getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

/**
 * The order the unified task list is read in: **work the agents are still holding comes before work that
 * has finished**, newest-updated first within each half.
 *
 * Sorting on `updatedDate` alone — which every surface used to do — ranks a task by when it last changed,
 * and finishing IS a change. So a task that completed ten minutes ago outranks one that has been running
 * for two hours, and a long-running or blocked task is pushed off the top of the screen by the very work
 * that is already over. Observed on a real account: an island reading "3 tasks in progress" above six rows
 * every one of which said Done, because the three live ones had sorted below the fold.
 *
 * Deliberately a two-way split, not a per-bucket ranking: `waiting` and `inProgress` are not ordered
 * against each other, because which of "the agent is working" and "the agent needs you" is more urgent is
 * a question about the task, not about its bucket — and the status tabs already separate them for anyone
 * who wants one or the other. Recency decides within each half, as before.
 *
 * Liveness deliberately plays NO part here. Whether a run is in flight is a property that flickers on a
 * ~minute timescale; letting it drive position would make rows jump between places under the reader's
 * cursor mid-scroll. It belongs on the row, as a marker, not in the ordering.
 *
 * Shared by all three surfaces: iOS `AgentTaskBucket.compareForTaskList`, Android `TASK_LIST_ORDER`. It
 * governs the account-wide list AND the island quick-view's peek on each — the quick-view was the last
 * holdout, sorting on `updatedDate` alone until it was brought onto this comparator.
 */
export function compareForTaskList(a: BucketableTask, b: BucketableTask, now: number = Date.now()): number {
  const aRank = listRank(a, now);
  const bRank = listRank(b, now);
  if (aRank !== bRank) return aRank - bRank;
  // Two scheduled rows read in the order they will actually happen — the soonest first. Recency of the
  // last write says nothing useful about work that has not started.
  if (aRank === SCHEDULED_RANK) {
    return (notBeforeMs(a) ?? 0) - (notBeforeMs(b) ?? 0);
  }
  return updatedMs(b) - updatedMs(a);
}

const SCHEDULED_RANK = 1;

/**
 * The three reading tiers: work in hand, then work waiting for its date, then work that has finished.
 * Scheduled sits between the two because it is neither — the agent is holding it, but nothing about it
 * will move before its date, so it must not push a live row down the screen.
 */
function listRank(task: BucketableTask, now: number): number {
  if (isSettled(task)) return 2;
  return isScheduledTask(task, now) ? SCHEDULED_RANK : 0;
}

/**
 * The windowed task list in reading order (see {@link compareForTaskList}). Copies before sorting — the
 * caller's array is very often a React Query cache entry, and sorting one in place mutates shared state.
 */
export function sortedForTaskList<T extends BucketableTask>(tasks: T[], now: number = Date.now()): T[] {
  return [...recentTasks(tasks, now)].sort((a, b) => compareForTaskList(a, b, now));
}

/** A task that can be attributed to the ORIGIN that raised it — a routine's firing, or a goal's layout. */
export interface GroupableTask extends BucketableTask {
  /** The routine (pulse identity) whose firing created this task; absent for one-off work. */
  pulseIdentityId?: string;
  /** The goal whose plan laid this task out; absent on work that serves no goal. */
  goalId?: string;
}

/** Where a task came from: which KIND of origin, and which one of that kind. */
export type TaskOriginKind = 'routine' | 'goal';

export interface TaskOrigin {
  kind: TaskOriginKind;
  id: string;
}

/**
 * The origin a task folds under, or null for one-off work.
 *
 * A routine WINS over a goal when a task carries both, which keeps the shipped routine fold exactly as it
 * was: a routine's repeated fires are the case that buries a screen, and a routine serving a goal still
 * fires hourly. The KIND is part of the answer, not decoration — it is what stops a goal fold and a
 * routine fold from merging into one another when they happen to sit next to each other in the list.
 */
export function taskOrigin(task: GroupableTask): TaskOrigin | null {
  if (task.pulseIdentityId) return { kind: 'routine', id: task.pulseIdentityId };
  if (task.goalId) return { kind: 'goal', id: task.goalId };
  return null;
}

/** Whether two tasks came from the SAME origin — same kind AND same id. */
function sameOrigin(a: GroupableTask, b: GroupableTask): boolean {
  const left = taskOrigin(a);
  const right = taskOrigin(b);
  return !!left && !!right && left.kind === right.kind && left.id === right.id;
}

/** One row of the rendered list: either a single task, or consecutive tasks of one origin collapsed. */
export interface TaskListRow<T extends GroupableTask> {
  /** Set only on a collapsed row — the routine every task in it came from. */
  pulseIdentityId?: string;
  /** Set only on a collapsed row of GOAL-laid-out tasks — the goal every task in it serves. */
  goalId?: string;
  /** The tasks this row stands for. Length 1 for an ordinary task row. */
  tasks: T[];
  /** Tasks in the row the user still has to deal with. Now that a group is single-outcome this equals
   *  the run count on a `waiting` group and zero on every other, but it stays because the collapsed
   *  header reads it and the native ports mirror the field. */
  needsYouCount: number;
  /** The outcome every run in a collapsed group shares — what lets the header say "7 Done" rather than
   *  an unlabelled "7 runs". Absent on an ordinary single-task row. */
  bucket?: AgentTaskBucket;
}

/** The origin a collapsed row folded on, or null on an ordinary single-task row. */
export function rowOrigin<T extends GroupableTask>(row: TaskListRow<T>): TaskOrigin | null {
  if (row.pulseIdentityId) return { kind: 'routine', id: row.pulseIdentityId };
  if (row.goalId) return { kind: 'goal', id: row.goalId };
  return null;
}

/**
 * How many consecutive tasks of ONE origin it takes before they collapse into a single row.
 *
 * **ONE threshold for both origins, deliberately** — the goal fold and the routine fold read it, on all
 * three surfaces. A fold threshold that differs by origin is invisible until somebody meets the odd case
 * cold, which is the exact class of bug this fold is here to fix.
 *
 * **Two, not three** (Ronny, 2026-08-31; this LOWERED the shipped routine threshold from 3). The reported
 * case was exactly two tasks: two goal-laid-out rows, no origin named on either, reading "In progress"
 * with nothing happening. A 3+ threshold leaves that case unfolded — the bug stays — and the same argument
 * applies to two consecutive runs of one routine, which is why routines moved with it rather than keeping
 * a second number.
 */
export const TASK_ORIGIN_GROUP_THRESHOLD = 2;

/**
 * Fold consecutive tasks of the same ORIGIN into one row, leaving everything else untouched.
 *
 * A routine that fires hourly produces an hourly task, and that is CORRECT — the work genuinely happened
 * and the record of it is the truth. Observed on a real account: one hourly monitor produced 15 tasks in a
 * day, each reporting a real check of 757 entities. The defect was never that those rows existed, it was
 * that they pushed the day's two pieces of one-off work off the screen. So this collapses the display and
 * changes nothing about what is stored: every run is still there, one expand away.
 *
 * The same rule now serves the OTHER origin: a goal lays several tasks out at once, and two of them
 * sitting unexplained above each other is what was reported as "very troubling" — the reader could not
 * tell why either row existed. Folding them under one line that names the goal answers that in one glance.
 *
 * **Consecutive, deliberately.** A one-off task landing between two runs of one origin breaks the group
 * rather than being swallowed by it, so nothing can hide inside a fold it does not belong to. Grouping is
 * by origin alone — a run's outcome does not split it — and `needsYouCount` is what keeps that safe: a run
 * that needs the user is counted on the header rather than buried.
 *
 * **A goal fold and a routine fold never merge**, because the key is the origin's KIND as well as its id
 * (see {@link taskOrigin}).
 *
 * Wired on web by `TasksBody`, which folds the FINAL rendered list (after filtering, tab selection and
 * ordering — "consecutive" means nothing before that) and draws a collapsed row as `TaskOriginGroup`.
 * iOS and Android carry the same fold on their own task lists — the threshold and the consecutive rule
 * are one product rule across the three surfaces, so a change here changes all three.
 */
export function groupTaskOrigins<T extends GroupableTask>(
  tasks: T[],
  threshold: number = TASK_ORIGIN_GROUP_THRESHOLD,
): TaskListRow<T>[] {
  const rows: TaskListRow<T>[] = [];
  const flush = (run: T[]) => {
    const origin = run[0] ? taskOrigin(run[0]) : null;
    if (run.length >= threshold && origin) {
      rows.push({
        ...(origin.kind === 'routine' ? { pulseIdentityId: origin.id } : { goalId: origin.id }),
        tasks: run,
        needsYouCount: countNeedsYou(run),
        bucket: bucketOfTask(run[0]),
      });
      return;
    }
    for (const task of run) rows.push({ tasks: [task], needsYouCount: countNeedsYou([task]) });
  };

  let run: T[] = [];
  for (const task of tasks) {
    // Same origin AND same outcome. Grouping on the origin alone folded a failed run in among six that
    // succeeded, and the collapsed row could then only honestly say "7 runs" — a count with no verdict,
    // which is what made a fold of finished work read as an unlabelled pile. A single-outcome group can
    // state its outcome ("7 Done"), and a run that ended differently gets its own row rather than
    // disappearing into one that misrepresents it.
    const continues = !!run[0] && sameOrigin(task, run[0]) && bucketOfTask(task) === bucketOfTask(run[0]);
    if (continues) {
      run.push(task);
      continue;
    }
    if (run.length > 0) flush(run);
    run = [task];
  }
  if (run.length > 0) flush(run);
  return rows;
}

function countNeedsYou(tasks: BucketableTask[]): number {
  return tasks.filter((task) => bucketOfTask(task) === 'waiting').length;
}

/**
 * The tabs the Agent Tasks strip renders, in order. Waiting / In progress / Done are the primary states
 * and always show even at zero; `failed` is exceptional and shows ONLY when something actually failed
 * inside the window — an empty Failed tab is noise, not information. Mirrors native
 * `AgentTaskBucket.visibleTabs` (native-apps/ios/Symphony/Navigation/AppRouter.swift).
 *
 * Note the window: a failure older than 30 days does not resurrect the tab, since the tab could not show
 * it anyway.
 */
export function visibleBuckets(tasks: BucketableTask[], now: number = Date.now()): AgentTaskBucket[] {
  const visible = [...ALWAYS_VISIBLE_BUCKETS];
  if (countInBucket(tasks, 'failed', now) > 0) visible.push('failed');
  return visible;
}

/**
 * Keeps a selection on a tab that is actually rendered. `failed` can vanish under the user — a refresh
 * that resolves the last failure un-renders the tab they were standing on — so the selection falls back
 * to `waiting` rather than dangling on a strip with nothing highlighted. Mirrors native's
 * `if !visibleTabs.contains(tab) { tab = .waiting }`.
 */
export function clampToVisibleBucket(
  bucket: AgentTaskBucket,
  tasks: BucketableTask[],
  now: number = Date.now(),
): AgentTaskBucket {
  return visibleBuckets(tasks, now).includes(bucket) ? bucket : 'waiting';
}

/**
 * The bucket a surface should open on: an explicit pick passes through (clamped to a rendered tab, so a
 * stale `?tab=failed` can't select a hidden one); anything else resolves to the first non-empty bucket so
 * a generic "see all tasks" entry lands on a populated tab rather than an empty "Waiting on you".
 * Mirrors native `firstNonEmpty` / `resolvedInitialTab`.
 */
export function resolveInitialBucket(
  requested: AgentTaskBucket | undefined,
  tasks: BucketableTask[],
  now: number = Date.now(),
): AgentTaskBucket {
  if (requested) return clampToVisibleBucket(requested, tasks, now);
  return visibleBuckets(tasks, now).find((bucket) => countInBucket(tasks, bucket, now) > 0) ?? 'waiting';
}

/** Parses a `?tab=` query value into a bucket; undefined when absent or unrecognised. */
export function parseBucketParam(value: unknown): AgentTaskBucket | undefined {
  return AGENT_TASK_BUCKETS.find((bucket) => bucket === value);
}

/** Whether a settled task set has nothing at all in the window — drives the empty state. */
export function agentTasksAreEmpty(tasks: BucketableTask[], now: number = Date.now()): boolean {
  return recentTasks(tasks, now).length === 0;
}

export type IslandColor = 'green' | 'blue' | 'orange';

/**
 * The bucket every working task WITHOUT an `agentId` folds into when counting distinct agents — the
 * Maestro. One bucket, deliberately: three unattributed live runs are the one assistant working three
 * times, not three agents.
 */
const MAESTRO_AGENT_BUCKET = '';

/**
 * The "agents at work" numbers, from the task rows themselves. A WORKING task is `runInFlight === true`
 * and nothing else — **absent is "not computed", never "not running"** (the list stamps the field on the
 * unsettled slice only, capped per response, and an older server stamps nothing at all), so a row without
 * the field can neither count as working nor assert idle. `workingAgents` is the DISTINCT agents behind
 * the working rows, with every missing-`agentId` row folding into ONE Maestro bucket.
 *
 * Web computes this client-side from the account-wide tasks list; the phones read the same two numbers
 * off the `agent-workspace-tasks` aggregate (`totalWorking` / `workingAgents`) — same rule, same names.
 */
export function workingCounts(
  tasks: { runInFlight?: boolean; agentId?: string }[],
  /**
   * The Maestro's in-flight NON-task work (`maestroActions` rows — a Wixel render, a free-run loop).
   * Each is one more working thing, and their worker is the Maestro — the SAME distinct agent the
   * agent-less tasks already fold into, so an action beside an agent-less working task is still ONE
   * agent. Mirrors the server aggregate's `summarizeWorkingNow(…, maestroActionCount)` exactly.
   */
  maestroActionCount: number = 0,
): {
  workingCount: number;
  workingAgents: number;
} {
  const agents = new Set<string>();
  let workingCount = 0;
  for (const task of tasks) {
    if (task.runInFlight !== true) continue;
    workingCount += 1;
    agents.add(task.agentId || MAESTRO_AGENT_BUCKET);
  }
  if (maestroActionCount > 0) {
    agents.add(MAESTRO_AGENT_BUCKET);
  }
  return { workingCount: workingCount + maestroActionCount, workingAgents: agents.size };
}

/**
 * The DISTINCT agents behind these tasks, in FIRST-SEEN order — the ordered-list companion to
 * {@link workingCounts}, so the island pill can draw one leading glyph per working / action-needed agent
 * in the same order the count was taken. Every missing `agentId` folds into the ONE Maestro bucket
 * (`''`), exactly as `workingCounts` folds it, so the ids the pill renders and the number it shows can
 * never disagree. An `''` entry is the Maestro / agent-less bucket and renders the Maestro mark.
 *
 * Order is the caller's: pass the activity-sorted working slice and the reading-ordered actionable slice
 * and you get each in that order, mirroring the server aggregate's `summarizeWorkingNow` per-agent
 * ordering. A trailing Maestro ACTION is the caller's to fold in — an action is not a task and does not
 * appear in `tasks`.
 */
export function orderedDistinctAgentIds(tasks: { agentId?: string }[]): string[] {
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const task of tasks) {
    const id = task.agentId || MAESTRO_AGENT_BUCKET;
    if (seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

/**
 * Keep the island's leading avatars in a STABLE order for the lifetime of the state. The id sets are
 * derived most-recent-activity-first, so a plain replace re-sorts the dots on every refetch as tasks are
 * touched. This preserves `previous`'s order for every id still present and appends genuine newcomers at
 * the end; an empty `incoming` returns empty, the natural reset when the state ends (starting the next
 * activation fresh). Idempotent — safe to apply on every render. Mirrors iOS/Android `stableAgentOrder`.
 */
export function stableAgentOrder(previous: readonly string[], incoming: readonly string[]): string[] {
  const incomingSet = new Set(incoming);
  const kept = previous.filter((id) => incomingSet.has(id));
  const keptSet = new Set(kept);
  return [...kept, ...incoming.filter((id) => !keptSet.has(id))];
}

/**
 * How many of these tasks are working RIGHT NOW — `runInFlight === true` only. The folded origin group's
 * "{count} working now" chip reads it, and the tri-state rule is the whole point: a fold of tasks whose
 * run state was never computed counts ZERO, it does not guess.
 */
export function workingNowCount(tasks: { runInFlight?: boolean }[]): number {
  return tasks.filter((task) => task.runInFlight === true).length;
}

/**
 * The island's colour. BLUE means "agents whose loop is EXECUTING right now" — `workingAgents > 0`, the
 * live-run verdict — NOT "has tasks in the in-progress bucket", which is what it used to mean: a PAUSED
 * loop, a queued PENDING task and a dead loop all bucket as in-progress while nothing is running. Orange
 * when nothing is (known to be) working but something waits; green when neither. An UNKNOWN working count
 * (the aggregate absent / the list not yet stamped) is zero here by construction — `workingCounts` only
 * counts `=== true` — so the island can never go blue from ignorance; it falls through to orange/green.
 * Mirrored 1:1 on native (`islandPillState`).
 */
export function resolveIslandState(counts: { workingAgents: number; waitingCount: number }): IslandColor {
  if (counts.workingAgents > 0) return 'blue';
  if (counts.waitingCount > 0) return 'orange';
  return 'green';
}
